import React, { useState, useEffect } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { COLUMN_FORMULAS } from './salesManagTableConfigs';
import {
  ProductTableScroll,
  ProductTableInner,
  PTRow,
  PTCell,
  PTHeadLabel,
  PTEmpty,
} from './SalesManagTab1Styled';

// config.columns 기반의 평면 테이블 (환불 내역 / 미수금 내역 공용).
// column: { label, width(px, flex면 생략), flex, align, tone, render(row, helpers) }
// expansion(선택): 복합 결제 행 갈매기 펼침 —
//   { isExpandable(row), getKey(row), fetch(row) => Promise<data>, renderDetail(data, row, helpers) }
const SalesManagFlatTable = ({ rows = [], config, helpers, expansion }) => {
  const { columns, tableWidth } = config;

  const [expandedKey, setExpandedKey] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  // 조회 결과가 바뀌면 펼침 상태·캐시 초기화 (페이지 이동/재검색)
  useEffect(() => {
    setExpandedKey(null);
    setDetailCache({});
    setLoadingKey(null);
  }, [rows]);

  const handleToggle = async row => {
    const key = expansion.getKey(row);
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (detailCache[key] === undefined) {
      setLoadingKey(key);
      try {
        const data = await expansion.fetch(row);
        setDetailCache(prev => ({ ...prev, [key]: data }));
      } catch (e) {
        setDetailCache(prev => ({ ...prev, [key]: [] }));
      } finally {
        setLoadingKey(null);
      }
    }
  };

  // 컬럼 render 가 갈매기(펼침 토글)를 그릴 수 있게 helpers 로 상태를 넘긴다.
  const helpersWithExpansion = expansion
    ? { ...helpers, expansion: { expandedKey, toggle: handleToggle, isExpandable: expansion.isExpandable, getKey: expansion.getKey } }
    : helpers;

  return (
    <ProductTableScroll>
      <ProductTableInner tableWidth={tableWidth}>
        {/* 헤더 */}
        <PTRow variant="head">
          {columns.map(col => (
            <PTCell key={col.label} cellWidth={col.width ? `${col.width}px` : undefined} flex={col.flex}>
              {COLUMN_FORMULAS[col.label] ? (
                <PTHeadLabel>
                  {col.label}
                  <Tooltip title={COLUMN_FORMULAS[col.label]} placement="top" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#737373', ml: '4px', verticalAlign: 'middle' }} />
                  </Tooltip>
                </PTHeadLabel>
              ) : (
                col.label
              )}
            </PTCell>
          ))}
        </PTRow>

        {rows.length === 0 && <PTEmpty>데이터가 없습니다.</PTEmpty>}

        {/* 바디 — 기본은 흰색 배경(그룹 펼침 detail 의 teal 강조색과 구분).
            환불/미수금 행만 rowTone 으로 옅은 배경을 깔아 성격을 구분한다. */}
        {rows.map((row, index) => {
          const rowKey = expansion ? expansion.getKey(row) : index;
          const isExpanded = expansion && expandedKey === rowKey;
          return (
            <React.Fragment key={index}>
              {/* 분할결제 행은 행 어디를 눌러도 펼침 토글(버튼 클릭은 제외). */}
              <PTRow
                variant="flat"
                rowTone={config.rowTone?.(row)}
                onClick={
                  expansion && expansion.isExpandable(row)
                    ? e => {
                        if (e.target.closest('button')) return;
                        handleToggle(row);
                      }
                    : undefined
                }
                style={
                  expansion && expansion.isExpandable(row)
                    ? {
                        cursor: 'pointer',
                        // 부모 행 배경은 일반 행과 동일 — 자식 라인 색으로만 분할을 구분한다(사용자 결정)
                        // 펼침 중엔 부모+자식 묶음 외곽을 진한 테두리로 감싼다(위/좌/우) — 환불 행은 붉은 계열
                        ...(isExpanded
                          ? (() => {
                              const edge = config.rowTone?.(row) === 'refund' ? '#E08A80' : '#4AB3BC';
                              return { boxShadow: `inset 0 2px 0 ${edge}, inset 2px 0 0 ${edge}, inset -2px 0 0 ${edge}` };
                            })()
                          : {}),
                      }
                    : undefined
                }>
                {columns.map(col => (
                  <PTCell
                    key={col.label}
                    cellWidth={col.width ? `${col.width}px` : undefined}
                    flex={col.flex}
                    align={col.align}
                    nowrap={col.nowrap}
                    tone={typeof col.tone === 'function' ? col.tone(row) : col.tone}>
                    {col.render(row, helpersWithExpansion)}
                  </PTCell>
                ))}
              </PTRow>
              {isExpanded &&
                (loadingKey === rowKey ? (
                  <PTRow variant="detail">
                    <PTCell flex>
                      <CircularProgress size={20} />
                    </PTCell>
                  </PTRow>
                ) : (
                  expansion.renderDetail(detailCache[rowKey] || [], row, helpers)
                ))}
            </React.Fragment>
          );
        })}
      </ProductTableInner>
    </ProductTableScroll>
  );
};

export default SalesManagFlatTable;
